import { Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { API_BASE_URL } from '@/utils/env';
import { getToken } from '@/utils/auth';
import { useTranslation } from 'react-i18next';

interface OnboardingGuardProps {
    children: React.ReactNode;
    requiredStep: 'country' | 'goal' | 'form1' | 'form2' | 'dashboard';
}

export default function OnboardingGuard({ children, requiredStep }: OnboardingGuardProps) {
    const { t } = useTranslation();
    const [isChecking, setIsChecking] = useState(true);
    const [redirectTo, setRedirectTo] = useState<string | null>(null);
    const location = useLocation();

    useEffect(() => {
        checkOnboardingStatus();
    }, []);

    const checkOnboardingStatus = async () => {
        try {
            // Check if user is authenticated
            const userStr = localStorage.getItem('user');
            const token = getToken();

            if (!userStr || !token) {
                setRedirectTo('/login');
                setIsChecking(false);
                return;
            }

            const user = JSON.parse(userStr);

            // Fetch user data from backend. If the server rejects due to UID mismatch
            // (403) try decoding the token to get the token's UID and retry.
            let response = await fetch(`${API_BASE_URL}/auth/user/${user.uid}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // If server returned 403, attempt to recover by using token's UID
            if (response.status === 403) {
                console.warn('⚠️ Onboarding check: received 403 for stored uid, attempting token-uid recovery');
                const decoded: any = parseJwt(token);
                const tokenUid = decoded?.user_id || decoded?.sub || decoded?.uid;
                if (tokenUid && tokenUid !== user.uid) {
                    // Sync localStorage with token UID to avoid repeated mismatches
                    try {
                        const syncedUser = { ...user, uid: tokenUid };
                        localStorage.setItem('user', JSON.stringify(syncedUser));
                        console.log('🔁 Synced localStorage user.uid to token UID', tokenUid);
                        // Retry fetch using the token-derived UID
                        response = await fetch(`${API_BASE_URL}/auth/user/${tokenUid}`, {
                            headers: { Authorization: `Bearer ${token}` }
                        });
                    } catch (err) {
                        console.error('Error syncing localStorage uid:', err);
                    }
                }
            }

            if (!response.ok) {
                setRedirectTo('/login');
                setIsChecking(false);
                return;
            }

            const data = await response.json();
            const userData = data?.user;

            // If backend returned incomplete data (e.g. missing country) prefer the
            // more-up-to-date localStorage user fields to avoid unnecessary redirects.
            const mergedUserData = { ...(user || {}), ...(userData || {}) };

            if (mergedUserData.emailVerified === false || mergedUserData.emailVerified === "false") {
                console.log('⚠️ Email not verified, redirecting to email verification');
                setRedirectTo('/email-verification');
                setIsChecking(false);
                return;
            }

            // Determine what step user should be on
            const nextStep = getNextOnboardingStep(mergedUserData);

            console.log('🔍 Onboarding check:', {
                requiredStep,
                nextStep,
                currentPath: location.pathname
            });

            // If user is trying to access a step they haven't unlocked yet
            if (shouldRedirect(requiredStep, nextStep)) {
                console.log(`⚠️ User trying to skip to ${requiredStep}, redirecting to ${nextStep}`);
                setRedirectTo(getStepPath(nextStep));
            } else {
                // User is on the correct step or has completed it
                setRedirectTo(null);
            }
        } catch (error) {
            console.error('Error checking onboarding status:', error);
            setRedirectTo('/login');
        } finally {
            setIsChecking(false);
        }
    };

    // Lightweight JWT parse helper (no external deps) to obtain UID from idToken
    const parseJwt = (token: string) => {
        try {
            const payload = token.split('.')[1];
            if (!payload) return null;
            // atob expects proper padding
            const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
            const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
            const decoded = JSON.parse(atob(padded));
            return decoded;
        } catch (err) {
            console.warn('Failed to parse JWT', err);
            return null;
        }
    };

    const getNextOnboardingStep = (userData: any): string => {
        if (!userData?.country) return 'country';
        // Capital raising users who completed capital assessment skip goal assessment
        if (userData?.capitalAssessment?.completedAt) return 'dashboard';
        if (!userData?.goalAssessment?.group) return 'goal';

        // Goal completed → dashboard is unlocked (same as choosing "no" on IR score screen)
        return 'dashboard';
    };

    const getStepPath = (step: string): string => {
        const stepPaths: Record<string, string> = {
            country: '/Countrysl',
            goal: '/readiness-journey',
            form1: '/form',
            form2: '/form2',
            dashboard: '/dashboard'
        };
        return stepPaths[step] || '/Countrysl';
    };

    const shouldRedirect = (requiredStep: string, nextStep: string): boolean => {
        const stepOrder = ['country', 'goal', 'form1', 'form2', 'dashboard'];
        const requiredIndex = stepOrder.indexOf(requiredStep);
        const nextIndex = stepOrder.indexOf(nextStep);

        // If user is trying to access a step ahead of where they should be
        return requiredIndex > nextIndex;
    };

    // Show loading state while checking
    if (isChecking) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">{t('onboardingGuard.redirecting')}</p>
                </div>
            </div>
        );
    }

    // Redirect if necessary
    if (redirectTo) {
        return <Navigate to={redirectTo} state={{ from: location }} replace />;
    }

    // User is on the correct step, render the content
    return <>{children}</>;
}
